import { useEffect, useRef, type RefObject } from 'react';
import { isMotionReduced } from '../systems/accessibility-settings';
import styles from './shield-gain-overlay.module.css';

export interface ShieldGainOverlayProps {
  amount: number;
  targetRef?: RefObject<HTMLElement | null>;
  subtitle?: string;
  onArrive?: () => void;
  onComplete: () => void;
}

const POP_MS = 2200;
const FLY_DELAY_MS = 1325;
const FLY_MS = 520;
const SAFETY_MS = FLY_DELAY_MS + FLY_MS + 400;
const FLY_EASING = 'cubic-bezier(.35,.05,.25,1)';

/**
 * Écu de fin de mission : apparition, onde, puis vol vers le compteur HUD.
 */
export function ShieldGainOverlay({
  amount,
  targetRef,
  subtitle = 'Compétence cyber',
  onArrive,
  onComplete,
}: ShieldGainOverlayProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const ecuRef = useRef<SVGSVGElement>(null);
  const arrivedRef = useRef(false);
  const completedRef = useRef(false);
  const flyElRef = useRef<SVGSVGElement | null>(null);
  const onArriveRef = useRef(onArrive);
  const onCompleteRef = useRef(onComplete);
  onArriveRef.current = onArrive;
  onCompleteRef.current = onComplete;

  const n = Math.max(1, amount);
  const label = n > 1 ? `+${n} Boucliers` : '+1 Bouclier';

  useEffect(() => {
    const overlay = overlayRef.current;
    const pop = popRef.current;
    const ecu = ecuRef.current;
    if (!overlay || !pop || !ecu) return;

    arrivedRef.current = false;
    completedRef.current = false;

    const markArrive = () => {
      if (arrivedRef.current) return;
      arrivedRef.current = true;
      onArriveRef.current?.();
    };

    const markComplete = () => {
      if (completedRef.current) return;
      completedRef.current = true;
      markArrive();
      onCompleteRef.current();
    };

    const dropFly = () => {
      flyElRef.current?.remove();
      flyElRef.current = null;
    };

    overlay.classList.remove(styles.play);
    pop.classList.remove(styles.play);
    void pop.offsetWidth;
    overlay.classList.add(styles.play);
    pop.classList.add(styles.play);

    const completeTimer = window.setTimeout(markComplete, POP_MS);

    if (document.visibilityState === 'hidden') {
      markArrive();
      return () => {
        window.clearTimeout(completeTimer);
        dropFly();
      };
    }

    const safetyTimer = window.setTimeout(markArrive, SAFETY_MS);
    const flyTimer = window.setTimeout(() => {
      if (isMotionReduced()) {
        markArrive();
        return;
      }
      const cibleRoot = targetRef?.current;
      const cible = (cibleRoot?.querySelector('svg') as HTMLElement | null) ?? cibleRoot;
      if (!cible || !ecu.isConnected) {
        markArrive();
        return;
      }

      const rs = overlay.getBoundingClientRect();
      const re = ecu.getBoundingClientRect();
      const rc = cible.getBoundingClientRect();
      const vol = ecu.cloneNode(true) as SVGSVGElement;
      vol.classList.add(styles.vol);
      vol.style.width = `${re.width}px`;
      vol.style.height = `${re.height}px`;
      vol.style.left = `${re.left - rs.left}px`;
      vol.style.top = `${re.top - rs.top}px`;
      overlay.appendChild(vol);
      flyElRef.current = vol;
      ecu.classList.add(styles.ecuGone);

      const dx = (rc.left + rc.width / 2) - (re.left + re.width / 2);
      const dy = (rc.top + rc.height / 2) - (re.top + re.height / 2);
      const anim = vol.animate(
        [
          { transform: 'translate(0,0) scale(1) rotate(0deg)' },
          {
            transform: `translate(${dx * 0.45}px, ${dy * 0.45 - 34}px) scale(.62) rotate(5deg)`,
            offset: 0.5,
          },
          { transform: `translate(${dx}px, ${dy}px) scale(.17) rotate(9deg)` },
        ],
        { duration: FLY_MS, easing: FLY_EASING, fill: 'forwards' },
      );

      const touche = () => {
        dropFly();
        markArrive();
      };
      anim.onfinish = touche;
      anim.oncancel = touche;
    }, FLY_DELAY_MS);

    return () => {
      window.clearTimeout(flyTimer);
      window.clearTimeout(safetyTimer);
      window.clearTimeout(completeTimer);
      dropFly();
    };
  }, [amount, targetRef]);

  return (
    <div ref={overlayRef} className={styles.overlay} role="alert" aria-live="assertive">
      <div className={styles.veil} aria-hidden />
      <div className={styles.stage}>
        <div ref={popRef} className={styles.pop}>
          <svg className={styles.onde} viewBox="0 0 160 160" aria-hidden>
            <circle cx="80" cy="80" r="22" />
          </svg>
          <svg ref={ecuRef} className={styles.ecu} viewBox="0 0 104 116" aria-hidden>
            <path
              className={styles.cadre}
              d="M52 3 L99 20 V63 c0 27-21 41-47 50 C26 104 5 90 5 63 V20 Z"
            />
            <path
              className={styles.int}
              d="M52 13 L89 26 V62 c0 21-16 32-37 39 C31 94 15 83 15 62 V26 Z"
            />
            <text className={styles.num} x="52" y="72" textAnchor="middle">
              {`+${n}`}
            </text>
          </svg>
          <div className={styles.copy}>
            <b>{label}</b>
            {subtitle ? <i>{subtitle}</i> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
